import { useState } from "react";
import { useI18n } from "../contexts/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import Counter from "../components/Counter";
import useSeo from "../hooks/useSeo";

export default function Hire() {
  const { t } = useI18n();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", phone: "", industry: "", num_positions: 1, message: "" });
  const [submitting, setSubmitting] = useState(false);

  useSeo({
    title: "Hire Qualified Talent in 48 Hours",
    description: "Tell ENCES Global what you need - bilingual recruiters in Union City NJ will deliver qualified candidates in under 48 hours across logistics, manufacturing and administration.",
    path: "/hire",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, type: "hire", num_positions: Number(form.num_positions) || 0 });
      toast.success(t.hire.success);
      setForm({ company_name: "", name: "", email: "", phone: "", industry: "", num_positions: 1, message: "" });
    } catch { toast.error(t.contact.error); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="page-hire">
      <section className="container-pad mx-auto max-w-[1400px] py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-brandred mb-3">{t.nav.hire}</div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-navy" data-testid="hire-title">{t.hire.title}</h1>
          <p className="mt-4 text-slate-600 text-lg max-w-xl">{t.hire.subtitle}</p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[{ v: 48, s: "h", label: "Average time-to-first candidate" }, { v: 95, s: "%", label: t.stats.satisfaction }, { v: 500, s: "+", label: t.stats.placements }, { v: 200, s: "+", label: t.stats.companies }].map((x, i) => (
              <div key={i} className="bg-soft rounded-2xl p-5">
                <div className="text-3xl font-extrabold text-navy"><Counter to={x.v} suffix={x.s} /></div>
                <div className="text-xs text-slate-600 mt-1">{x.label}</div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-100 rounded-[24px] p-7 md:p-9 shadow-soft space-y-5" data-testid="hire-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="h-company">{t.hire.company} *</Label>
              <Input id="h-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-company" />
            </div>
            <div>
              <Label htmlFor="h-name">{t.hire.contact} *</Label>
              <Input id="h-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-contact-name" />
            </div>
            <div>
              <Label htmlFor="h-email">{t.hire.email} *</Label>
              <Input id="h-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-email" />
            </div>
            <div>
              <Label htmlFor="h-phone">{t.hire.phone}</Label>
              <Input id="h-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-phone" />
            </div>
            <div>
              <Label htmlFor="h-industry">{t.hire.industry}</Label>
              <Input id="h-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-industry" />
            </div>
            <div>
              <Label htmlFor="h-pos">{t.hire.positions}</Label>
              <Input id="h-pos" type="number" min="1" value={form.num_positions} onChange={(e) => setForm({ ...form, num_positions: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-positions" />
            </div>
          </div>
          <div>
            <Label htmlFor="h-msg">{t.hire.message} *</Label>
            <Textarea id="h-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" data-testid="hire-message" />
          </div>
          <button type="submit" disabled={submitting} className="w-full px-6 py-4 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold shadow-lg shadow-red-500/20 disabled:opacity-60" data-testid="hire-submit">
            {submitting ? t.common.loading : t.hire.submit}
          </button>
        </form>
      </section>
    </div>
  );
}
